<template>
  <div
    class="pb-12 pt-12 w-full flex flex-col justify-center items-center px-4"
  >
    <!-- 1. Header Row: Status Label & Percentage -->
    <div class="w-full max-w-lg mb-3 flex justify-between items-end min-h-[20px]">
      <div class="flex items-center gap-3">
        <i
          class="fas fa-circle-notch fa-spin text-lg opacity-70 shrink-0"
          :class="[
            theme === 'emerald'
              ? 'text-emerald-600 dark:text-emerald-500'
              : theme === 'purple'
                ? 'text-purple-600 dark:text-purple-500'
                : theme === 'amber'
                  ? 'text-amber-600 dark:text-amber-500'
                  : 'text-blue-600 dark:text-blue-500',
          ]"
        ></i>
        <em class="text-sm font-bold text-gray-700 dark:text-gray-300 truncate">{{
          status || 'Initializing answer...'
        }}</em>
      </div>
      <span
        class="text-xs font-mono text-gray-500 dark:text-gray-400 transition-opacity duration-300"
        :class="totalStepCount > 1 ? 'opacity-100' : 'opacity-0'"
      >
        {{ totalStepCount > 1 ? Math.round((Math.min(currentStepNumber, totalStepCount) / totalStepCount) * 100) : 0 }}%
      </span>
    </div>

    <!-- 2. Tracking Bar: Fills or Pulsates (Stable Height) -->
    <div
      class="w-full max-w-lg h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden shadow-inner"
    >
      <div
        class="h-full rounded-full transition-all duration-500 relative"
        :class="[
          theme === 'emerald'
            ? 'bg-emerald-600 dark:bg-emerald-500'
            : theme === 'purple'
              ? 'bg-purple-600 dark:bg-purple-500'
              : theme === 'amber'
                ? 'bg-amber-600 dark:bg-amber-500'
                : 'bg-blue-600 dark:bg-blue-500',
          (!totalStepCount || totalStepCount <= 1) ? 'w-full animate-pulse opacity-40' : ''
        ]"
        :style="totalStepCount > 1 ? `width: ${Math.min((currentStepNumber / totalStepCount) * 100, 100)}%` : ''"
      >
        <div
          class="absolute inset-0 bg-white/20 dark:bg-white/10 w-full animate-pulse"
        ></div>
      </div>
    </div>

    <!-- 3. Substatus Message (Fades in-situ) -->
    <div class="w-full max-w-lg mt-3 text-center min-h-[16px]">
      <p
        class="text-xs text-gray-500 dark:text-gray-400 italic relative transition-all duration-500"
        :class="{
          'opacity-0 translate-y-2': substatusChanged,
          'opacity-100 translate-y-0': !substatusChanged,
          'opacity-0': totalStepCount > 1 && Math.round((Math.min(currentStepNumber, totalStepCount) / totalStepCount) * 100) === 100
        }"
      >
        {{ substatusDisplayText }}
      </p>
    </div>

    <!-- 4. Footer Actions: Timer clock & Cancel Button (Consistent Position) -->
    <div
      v-if="timeElapsedDisplayText || (showReset && hasCancelListener)"
      class="flex items-center gap-3 mt-4"
    >
      <span
        v-if="timeElapsedDisplayText"
        class="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded"
      >
        <i class="fa-regular fa-clock"></i> {{ timeElapsedDisplayText }}
      </span>
      <button
        v-if="showReset && hasCancelListener"
        class="text-xs font-bold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:underline transition-colors uppercase tracking-wider"
        @click="$emit('cancel')"
      >
        Cancel
      </button>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    totalStepCount: Number,
    currentStepNumber: Number,
    status: String,
    substatus: String,
    theme: {
      type: String,
      default: "blue",
    },
    beganAt: String,
    showReset: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["cancel"],
  computed: {
    hasCancelListener() {
      return !!this.$attrs.onCancel;
    },
  },
  data() {
    return {
      substatusChangeTimeout: null,
      substatusChanged: false,
      substatusDisplayText: null,
      timeElapsedDisplayText: null,
      elapsedTimer: null,
    };
  },
  watch: {
    substatus(newVal) {
      this.substatusChangeTimeout = null;
      this.substatusChanged = true;
      this.substatusChangeTimeout = setTimeout(() => {
        this.substatusDisplayText = newVal;
        this.substatusChanged = false;
      }, 500);
    },
    beganAt: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          this.startTimer();
        } else {
          this.stopTimer();
        }
      },
    },
  },
  mounted() {
    if (this.beganAt) {
      this.startTimer();
    }
  },
  unmounted() {
    this.stopTimer();
  },
  methods: {
    startTimer() {
      this.stopTimer();
      this.updateElapsedTime();
      this.elapsedTimer = setInterval(this.updateElapsedTime, 1000);
    },
    stopTimer() {
      if (this.elapsedTimer) {
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
      }
    },
    updateElapsedTime() {
      if (!this.beganAt) {
        this.timeElapsedDisplayText = null;
        return;
      }
      const startString = this.beganAt.endsWith("Z")
        ? this.beganAt
        : `${this.beganAt}Z`;
      const startTime = new Date(startString).getTime();
      const now = new Date().getTime();
      const diffInSeconds = Math.floor((now - startTime) / 1000);

      if (diffInSeconds < 0 || isNaN(diffInSeconds)) {
        this.timeElapsedDisplayText = null;
        return;
      }

      const minutes = Math.floor(diffInSeconds / 60);
      const seconds = diffInSeconds % 60;
      this.timeElapsedDisplayText = `${minutes}:${seconds
        .toString()
        .padStart(2, "0")}`;
    },
  },
};
</script>
